import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronRight, Scale, Clock, PackageX, Megaphone } from "lucide-react";
import IncorrectWeightChargesPanel, { INCORRECT_WEIGHT_ACTION_TYPE } from "./IncorrectWeightChargesPanel";
import DemurrageChargesPanel, { DEMURRAGE_ACTION_TYPE } from "./DemurrageChargesPanel";
import LostDamagePanel, { LOST_DAMAGE_ACTION_TYPE } from "./LostDamagePanel";
import CommonClientNotificationPanel, { CLIENT_NOTIFICATION_ACTION_TYPE } from "./CommonClientNotificationPanel";
import TemplateSendHistory from "./TemplateSendHistory";

/**
 * General Email Templates — opened from the Configs tab of Notification Setup.
 * A picker of ad-hoc email templates; choosing one renders that template's own
 * form (which owns its recipients, preview and send). Navigation state is held by
 * the page (`selected`) so switching tabs and coming back returns to the same view.
 *
 * To add a template: give it a component and flip `available` — the picker and
 * breadcrumb pick it up from this list. A template's `id` is its notification_configs
 * action_type (where its saved recipients live).
 */
const TEMPLATES = [
  {
    id: INCORRECT_WEIGHT_ACTION_TYPE,
    title: "Incorrect Weight Charges Email Notification",
    pageTitle: "Incorrect Weight Charges Email Notification Template",
    description: "Report shipments whose dimensions or weight were recorded incorrectly — one or more AWBs, live preview, Trigger Now.",
    icon: Scale,
    available: true,
    Component: IncorrectWeightChargesPanel,
  },
  {
    id: DEMURRAGE_ACTION_TYPE,
    title: "Demurrage Charges Email Notification",
    pageTitle: "Demurrage Charges Email Notification Template",
    description: "Ask Delhivery to clarify demurrage charges applied to a list of LRNs — live preview, Trigger Now.",
    icon: Clock,
    available: true,
    Component: DemurrageChargesPanel,
  },
  {
    id: LOST_DAMAGE_ACTION_TYPE,
    title: "Lost and Damage Email Notification",
    pageTitle: "Lost and Damage Email Notification Template",
    description: "Report an LRN as damaged or lost, with proof images and the ticket attached — live preview, Trigger Now.",
    icon: PackageX,
    available: true,
    Component: LostDamagePanel,
  },
  {
    id: CLIENT_NOTIFICATION_ACTION_TYPE,
    title: "Common Client Notification",
    pageTitle: "Common Client Notification",
    description: "Send operational announcements about embargoes, festival delays, service changes, and revised commercial terms.",
    icon: Megaphone,
    available: true,
    Component: CommonClientNotificationPanel,
  },
];

/** action_types whose configs are managed here, not as generic Configs cards. */
export const TEMPLATE_ACTION_TYPES = TEMPLATES.filter((t) => t.available).map((t) => t.id);

/** Page heading for an open template (null when none is open). */
export const templatePageTitle = (id) =>
  TEMPLATES.find((t) => t.id === id && t.available)?.pageTitle || null;

export default function GeneralEmailTemplates({ selected, onSelect, onClose, onViewLogs, user, configs, loading, onConfigSaved }) {
  const template = TEMPLATES.find((t) => t.id === selected && t.available);
  const TemplateForm = template?.Component;

  return (
    <div className="space-y-6" data-testid="general-email-templates">
      <nav aria-label="Breadcrumb" className="flex flex-wrap items-center gap-1 text-sm text-gray-500 min-w-0">
        <button type="button" onClick={onClose} className="hover:text-teal-700">Configs</button>
        <ChevronRight className="w-4 h-4 shrink-0" />
        {template ? (
          <>
            <button type="button" onClick={() => onSelect(null)} className="hover:text-teal-700">General Email Templates</button>
            <ChevronRight className="w-4 h-4 shrink-0" />
            <span className="text-gray-900 font-medium break-words">{template.title}</span>
          </>
        ) : (
          <span className="text-gray-900 font-medium">General Email Templates</span>
        )}
      </nav>

      {TemplateForm ? (
        <TemplateForm user={user} configs={configs} loading={loading} onConfigSaved={onConfigSaved} />
      ) : (
        <div className="space-y-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">General Email Templates</h2>
            <p className="text-sm text-gray-500 mt-1">Choose a template to compose, preview and send an email.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {TEMPLATES.map((t) => {
              const Icon = t.icon;
              const body = (
                <CardContent className="p-5 flex items-start gap-3">
                  <div className={`p-2 rounded-lg shrink-0 ${t.available ? "bg-teal-100 text-teal-700" : "bg-gray-100 text-gray-400"}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <p className={`font-medium ${t.available ? "text-gray-900" : "text-gray-500"}`}>{t.title}</p>
                    <p className="text-sm text-gray-500 mt-1">{t.description}</p>
                    {!t.available && <Badge variant="secondary" className="mt-2 text-xs">Coming soon</Badge>}
                  </div>
                </CardContent>
              );
              return t.available ? (
                <button key={t.id} type="button" onClick={() => onSelect(t.id)}
                  className="text-left rounded-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
                  data-testid={`template-${t.id}`}>
                  <Card className="h-full hover:shadow-md hover:border-teal-300 transition-shadow">{body}</Card>
                </button>
              ) : (
                <Card key={t.id} aria-disabled="true" className="h-full border-dashed bg-gray-50/60"
                  data-testid={`template-${t.id}`}>{body}</Card>
              );
            })}
          </div>
          <TemplateSendHistory onViewLogs={onViewLogs} />
        </div>
      )}
    </div>
  );
}
